import { useMfaLoginStore } from '@features/shared/stores/use-mfa-login-store';
import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useValidatorStore } from '../stores';
import { useLoginStore } from '@features/shared';
import { useGigyaStore } from '@ciam/gigya-api';
import { useModal } from '@ciam/ui';
import { useTranslation } from '@ciam/i18n';

export const MfaCheck = () => {
  const { openModal } = useModal();
  const { t } = useTranslation(['login', 'schemas']);
  const isGigyaScriptLoaded = useGigyaStore((state) => state.isGigyaScriptLoaded);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useLoginStore((state) => state.currentUser);
  const { setMfaSession, isSkipMfaLogin, hasMfaSession, executeMfaLogin, validateJwtTokenResponse } =
    useMfaLoginStore();
  const { setValidateStep } = useValidatorStore();

  const jwtTokenResponse = searchParams.get('jwtTokenResponse') as string;

  const removeJwtToken = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('jwtTokenResponse');
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const handleCheckMfa = useCallback(async () => {
    const channel = searchParams.get('channel') as string;
    // [STEP 2] MFA 스킵 채널인지 확인
    const isSkipMfaLoginResult = await isSkipMfaLogin({ currentChannel: channel });
    if (isSkipMfaLoginResult.success && isSkipMfaLoginResult.skip) {
      return setValidateStep('password-check');
    }

    // [STEP 3] MFA 세션 확인
    const sessionResult = await hasMfaSession({ email: currentUser.email });
    if (sessionResult.success && sessionResult.hasSession) {
      return setValidateStep('password-check');
    }

    // [STEP 3-1] MFA 세션이 없으면 MFA 로그인 실행
    const excuteMfaLoginResult = await executeMfaLogin({ email: currentUser.email, uid: currentUser.uid });
    if (excuteMfaLoginResult.success && excuteMfaLoginResult.jwtTokenResponse) {
      return window.location.assign(excuteMfaLoginResult.jwtTokenResponse);
    }

    // [STEP 3-2] MFA 세션이 있으므로 비밀번호 확인 단계로 이동
    return setValidateStep('password-check');
  }, [
    currentUser.email,
    currentUser.uid,
    isSkipMfaLogin,
    hasMfaSession,
    executeMfaLogin,
    setValidateStep,
    searchParams,
  ]);

  const handleValidateJwtTokenResponse = useCallback(async () => {
    const validateResult = await validateJwtTokenResponse({
      jwtTokenResponse: jwtTokenResponse as string,
      isBioOnly: searchParams.get('isBioOnly') === 'true',
    });

    if (validateResult.success && validateResult.isValid) {
      setMfaSession({ email: currentUser.email });
      return setValidateStep('password-check');
    }

    return openModal({
      type: 'error',
      title: t('errors.mfa-error-title'),
      description: [t('errors.mfa-error-description')],
      onCancel: () => {
        removeJwtToken();
      },
    });
  }, [
    currentUser.email,
    jwtTokenResponse,
    openModal,
    removeJwtToken,
    searchParams,
    setMfaSession,
    setValidateStep,
    t,
    validateJwtTokenResponse,
  ]);

  useEffect(() => {
    if (!isGigyaScriptLoaded) return;

    if (jwtTokenResponse) {
      handleValidateJwtTokenResponse();
    } else {
      handleCheckMfa();
    }
  }, [handleCheckMfa, handleValidateJwtTokenResponse, isGigyaScriptLoaded, jwtTokenResponse]);

  return null;
};
